import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import { ShoppingCart, ArrowRight, Check, Star, Zap, Globe, Search, MapPin, Target, BarChart2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CATEGORIES = [
  {
    id: 'klein',
    label: 'Kleine Optimalisaties',
    desc: 'Direct uitvoerbaar, snel resultaat',
    products: [
      { id: 'meta_title_fix', name: 'Meta Title Fix', price: '€19,95', desc: 'Optimaliseer uw paginatitel voor betere click-through in Google.', icon: Search },
      { id: 'meta_desc_fix', name: 'Meta Description Fix', price: '€19,95', desc: 'Schrijf een overtuigende meta-omschrijving die meer klikken oplevert.', icon: Search },
      { id: 'cta_optimalisatie', name: 'CTA Optimalisatie', price: '€24,95', desc: 'Verbeter uw call-to-action voor hogere conversies.', icon: Target },
      { id: 'review_antwoord', name: 'Reviewantwoord', price: '€14,95', desc: 'Professioneel antwoord opstellen voor uw online reviews.', icon: Star },
      { id: 'keyword_mapping', name: 'Keyword Mapping', price: '€14,95', desc: 'Koppel de juiste zoekwoorden aan de juiste pagina\'s.', icon: Search },
    ]
  },
  {
    id: 'middel',
    label: 'Middelgrote Optimalisaties',
    desc: 'Diepgaande verbeteringen per onderdeel',
    products: [
      { id: 'seo_pagina', name: 'SEO Pagina Optimalisatie', price: '€99,95', desc: 'Volledige on-page SEO optimalisatie van één pagina.', icon: Search },
      { id: 'lokaal_profiel', name: 'Lokale Profieloptimalisatie', price: '€119,95', desc: 'Google Bedrijfsprofiel volledig geoptimaliseerd.', icon: MapPin },
      { id: 'homepage_herschrijven', name: 'Homepage Herschrijven', price: '€79,95', desc: 'Professionele, conversiegerichte homepage teksten.', icon: Globe },
      { id: 'landingspagina_content', name: 'Landingspagina + Content', price: '€129,95', desc: 'Complete landingspagina met conversiegerichte content.', icon: Globe },
    ]
  },
  {
    id: 'bundle',
    label: 'Bundels',
    desc: 'Complete oplossingen voor één uitdaging',
    products: [
      { id: 'website_basis', name: 'Website Basisoptimalisatie', price: '€149,95', desc: 'Complete technische en content check van uw website.', icon: Globe, popular: false },
      { id: 'homepage_upgrade', name: 'Homepage Conversie Upgrade', price: '€249,95', desc: 'Transformeer uw homepage naar een conversie-machine.', icon: Globe, popular: true },
      { id: 'seo_basis', name: 'SEO Basisoptimalisatie', price: '€199,95', desc: 'Sterke SEO-basis voor organische groei.', icon: Search, popular: false },
      { id: 'lokaal_basis', name: 'Lokale Vindbaarheid Optimalisatie', price: '€179,95', desc: 'Word gevonden door klanten in uw regio.', icon: MapPin, popular: false },
      { id: 'gbp_setup', name: 'Google Bedrijfsprofiel Setup', price: '€199,95', desc: 'Complete inrichting en optimalisatie van uw GBP.', icon: MapPin, popular: false },
      { id: 'reputatie_booster', name: 'Review & Reputatie Booster', price: '€99,95', desc: 'Verbeter uw online reputatie structureel.', icon: Star, popular: false },
      { id: 'quick_fix_bundel', name: 'Complete Quick Fix Bundel', price: '€499,95', desc: 'Alles tegelijk: website, SEO, lokaal en reputatie.', icon: Zap, popular: true },
    ]
  },
];

export default function DienstenModule() {
  const { api } = useAuth();
  const plan = usePlan();
  const [activeTab, setActiveTab] = useState('klein');
  const [ordering, setOrdering] = useState(null);

  const isPaid = plan.isPaid;
  const isManaged = plan.isManaged;

  const handleOrder = async (product) => {
    setOrdering(product.id);
    try {
      const originUrl = window.location.origin;
      const r = await api('post', '/api/checkout/create-session', {
        service_id: product.id,
        origin_url: originUrl,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('Betaallink kon niet worden aangemaakt.');
      }
    } catch (err) {
      toast.error('Bestellen mislukt. Probeer opnieuw.');
    } finally {
      setOrdering(null);
    }
  };

  const activeCat = CATEGORIES.find(c => c.id === activeTab);

  return (
    <div className="max-w-5xl">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: "'Sora', sans-serif" }}>Diensten & Optimalisaties</h1>
        <p className="text-[#64748b] text-sm mt-1">Losse diensten — door ons team uitgevoerd, eenmalig te bestellen</p>
      </div>

      {/* Managed banner: deze klant heeft al Managed AI - geen losse diensten nodig */}
      {isManaged && (
        <div data-testid="diensten-managed-banner" className="mb-5 p-4 rounded-2xl bg-[#0f172a] text-white" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Crown className="w-5 h-5 text-[#D4A017] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold">U heeft Managed AI — losse diensten zijn niet nodig</p>
            <p className="text-xs text-white/60 mt-0.5">
              Onze AI + team voeren deze optimalisaties al voor u uit binnen uw abonnement, voor zover de koppelingen en technische mogelijkheden dat toelaten.
            </p>
          </div>
          <Link to="/platform/beheer" className="flex-shrink-0 text-xs font-bold text-[#D4A017] whitespace-nowrap">
            Naar Managed Dashboard →
          </Link>
        </div>
      )}

      {/* Verschil uitleg banner (alleen voor free / self-service) */}
      {!isManaged && (
      <div data-testid="diensten-info-banner" className="mb-5 p-4 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc]" style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <p className="text-xs font-bold text-[#0f172a] mb-1">Hoe werkt het?</p>
          <p className="text-xs text-[#64748b] leading-relaxed">
            Deze diensten zijn <strong>losse, eenmalige opdrachten</strong> die ons team voor u uitvoert.
            Dit is <strong>niet</strong> het IMPERIA AI Radar abonnement.
            Na betaling gaan wij direct aan de slag en leveren binnen 5 werkdagen.
          </p>
        </div>
        {!isPaid && (
          <Link to="/platform/checkout" className="flex-shrink-0 text-xs font-bold text-[#D4A017] hover:underline whitespace-nowrap" style={{ marginTop: '2px' }}>
            Connected Self-Service · €14,95/mnd →
          </Link>
        )}
      </div>
      )}

      {/* Tabs en service-grid: alleen voor non-managed users */}
      {!isManaged && (
      <>
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setActiveTab(c.id)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all ${activeTab === c.id ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] border border-gray-200 hover:border-[#0f172a]'}`}>
            {c.label}
          </button>
        ))}
      </div>

      {activeCat && (
        <>
          <p className="text-[#64748b] text-sm mb-4">{activeCat.desc}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeCat.products.map(p => {
              const Icon = p.icon;
              return (
                <div key={p.id} className={`bg-white rounded-2xl border p-5 hover:shadow-md transition-all relative ${p.popular ? 'border-[#D4A017]/40' : 'border-gray-100'}`}>
                  {p.popular && <div className="absolute -top-2 right-4 bg-[#D4A017] text-[#0f172a] text-[10px] font-bold px-3 py-0.5 rounded-full">Populair</div>}
                  <div className="w-10 h-10 bg-[#f8fafc] rounded-xl flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#D4A017]" />
                  </div>
                  <h3 className="font-bold text-[#0f172a] text-sm mb-1">{p.name}</h3>
                  <p className="text-[#64748b] text-xs mb-3 leading-relaxed">{p.desc}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-[#D4A017] font-bold">{p.price}</span>
                    <button data-testid={`order-btn-${p.id}`} onClick={() => handleOrder(p)} disabled={ordering === p.id}
                      className="flex items-center gap-1.5 bg-[#0f172a] hover:bg-[#1e293b] text-white font-bold text-xs px-4 py-2 rounded-xl transition-all disabled:opacity-60">
                      {ordering === p.id ? '...' : <><ShoppingCart size={12} /> Betaal & bestel</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Meer producten links */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: 'Maandelijkse Pakketten', desc: 'Doorlopende marketing', href: '/platform/pakketten', price: 'Vanaf €750/mnd' },
          { label: 'Leads Kopen', desc: 'Zakelijke leads', href: '/platform/leads', price: 'Vanaf €295' },
          { label: 'Website Laten Bouwen', desc: 'Professionele websites', href: '/platform/website', price: 'Vanaf €999' },
          { label: 'Campagnes', desc: 'Omnichannel campagnes', href: '/platform/campagnes', price: 'Op maat' },
          { label: 'Offline Marketing', desc: 'Billboards, radio, sponsoring', href: '/platform/offline', price: 'Op maat' },
        ].map(l => (
          <Link key={l.label} to={l.href}
            className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md hover:border-[#D4A017]/30 transition-all group flex items-center justify-between">
            <div>
              <p className="font-bold text-[#0f172a] text-sm group-hover:text-[#D4A017] transition-colors">{l.label}</p>
              <p className="text-[#64748b] text-xs">{l.desc}</p>
              <p className="text-[#D4A017] font-bold text-xs mt-1">{l.price}</p>
            </div>
            <ArrowRight size={16} className="text-[#94a3b8] group-hover:text-[#D4A017] transition-colors" />
          </Link>
        ))}
      </div>
      </>
      )}
    </div>
  );
}
